/**
 * dashboard/students/$studentId.tsx — N-27 Per-student detail for tutor.
 *
 * Lives at `/dashboard/students/$studentId`. Renders the same read-only
 * progress surface as N-17 (parent) but accessible only to tutors
 * who own at least one class containing this student. Includes the
 * same "Dilihat oleh tutor" banner for transparency.
 */

import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../components/ui/StatePanel.js';
import { ProgressHeader } from '../../../components/student/ProgressHeader.js';
import {
  freshSessionQuery,
  tutorStudentDetailQuery,
  type ProgressAggregate,
} from '../../../lib/api/queries.js';

export const Route = createFileRoute('/dashboard/students/$studentId')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'admin') {
      throw redirect({ to: '/admin' });
    }
    if (user.role === 'parent') {
      throw redirect({ to: '/parent' });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  component: TutorStudentDetailPage,
});

function TutorStudentDetailPage(): ReactNode {
  const { studentId } = Route.useParams();
  const detail = useQuery(tutorStudentDetailQuery(studentId));

  if (detail.isLoading) {
    return (
      <main className="page-shell">
        <p className="form-message">Memuat progres siswa…</p>
      </main>
    );
  }

  if (!detail.data?.ok) {
    const msg =
      detail.data?.error?.message ?? 'Gagal memuat progres siswa ini.';
    const isForbidden =
      detail.data?.error?.code === 'forbidden' ||
      // worker-pool / dev mode often returns empty data when not yet
      // seeded — show friendly empty state instead of error.
      false;
    if (isForbidden) {
      return (
        <main className="page-shell">
          <StatePanel
            variant="error"
            title="Akses tidak tersedia"
            description={msg}
            action={
              <Link to="/dashboard/students" className="button button-secondary">
                Kembali ke daftar siswa
              </Link>
            }
          />
        </main>
      );
    }
    return (
      <main className="page-shell">
        <StatePanel
          variant="empty"
          title="Belum ada data"
          description={msg}
          action={
            <Link to="/dashboard/students" className="button button-secondary">
              Kembali ke daftar siswa
            </Link>
          }
        />
      </main>
    );
  }

  const data = detail.data.data;
  const student = data.student;
  const aggregate: ProgressAggregate = {
    gamesStarted: student.gamesAssigned,
    gamesCompleted: student.gamesCompleted,
    totalStars: student.totalStars,
    worksheetsCompleted: student.worksheetsCompleted,
    totalWorksheets: student.worksheetsAssigned,
    streakDays: 0,
    badgesEarned: 0,
  };

  return (
    <main
      className="page-shell"
      data-testid="tutor-student-detail-page"
      data-view-mode="tutor"
    >
      <div
        className="tutor-view-banner"
        role="status"
        data-testid="tutor-view-banner"
      >
        <span className="tutor-view-banner-icon" aria-hidden="true">
          👩‍🏫
        </span>
        <span>
          Dilihat oleh tutor. Anda sedang melihat progres belajar{' '}
          <strong>{student.name}</strong>.
        </span>
      </div>

      <PageHeader
        eyebrow="Progres siswa"
        title={student.name}
        description={
          student.classes.length === 0
            ? 'Siswa belum terdaftar di kelas manapun.'
            : `Kelas: ${student.classes.map((c) => c.className).join(', ')}.`
        }
        action={
          <Link to="/dashboard/students" className="button button-secondary">
            ← Kembali ke daftar siswa
          </Link>
        }
      />

      <ProgressHeader studentName={student.name} aggregate={aggregate} />

      <section className="section">
        <h2 className="section-title">Game yang dimainkan</h2>
        {data.perGame.length === 0 ? (
          <StatePanel
            variant="empty"
            title="Belum ada game yang dimainkan"
            description={`${student.name} belum memainkan game apapun.`}
          />
        ) : (
          <ul className="card-grid" data-testid="tutor-student-games">
            {data.perGame.map((g) => (
              <li key={g.gameId} className="card">
                <p className="card-meta">{g.gameId}</p>
                <h3 className="card-title">{g.title}</h3>
                <p className="card-meta">
                  {g.completedLevels}/{g.totalLevels} level · {g.starsEarned} bintang
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="section">
        <h2 className="section-title">Worksheet yang sudah dibuka</h2>
        {data.perWorksheet.length === 0 ? (
          <StatePanel
            variant="empty"
            title="Belum ada worksheet yang dikerjakan"
            description={`${student.name} belum membuka worksheet apapun di kelas kamu.`}
          />
        ) : (
          <ul className="card-grid" data-testid="tutor-student-worksheets">
            {data.perWorksheet.map((w) => (
              <li key={w.worksheetId} className="card">
                <h3 className="card-title">{w.title}</h3>
                <p className="card-meta">{w.worksheetId}</p>
                <p>
                  {w.completed
                    ? `Selesai ${w.completedAt ? new Date(w.completedAt).toLocaleDateString('id-ID') : ''}`
                    : 'Belum selesai'}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {data.recentActivity.length > 0 ? (
        <section className="section">
          <h2 className="section-title">Aktivitas terbaru</h2>
          <ul className="activity-feed" data-testid="tutor-student-activity">
            {data.recentActivity.map((event) => (
              <li key={event.id} className="activity-feed-item">
                <strong>{event.label}</strong>
                <span className="activity-feed-time">
                  {' '}
                  · {new Date(event.occurredAt).toLocaleString('id-ID')}
                </span>
                {event.detail ? (
                  <p className="activity-feed-detail">{event.detail}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </main>
  );
}
